import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { usePopper } from "react-popper";
import { graphql, RelayEnvironmentProvider } from "react-relay";

import { faExclamationCircle, faPlus, faSpinner, faSync } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { debounce, uniq } from "lodash";
import { Node as ProseNode } from "prosemirror-model";
import { EditorState, Plugin, PluginKey, Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";
import invariant from "tiny-invariant";
import { v4 } from "uuid";

import { AGENDA_BUILDER_FEATURE_FLAG, type HeaderContentType } from "~/agendaBuilder";
import { DismissibleElementsContext } from "~/common/DismissibleElements";
import ViewportContext from "~/common/ViewportContext";
import { getHTMLElement } from "~/lib/dom";
import useBlur from "~/lib/hooks/useBlur";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Tooltip } from "~/ui";
import Button from "~/ui/button";
import DismissibleTooltip from "~/ui/Tooltip/DismissibleTooltip";

import { Extension } from "../extension";
import { blockDescendants } from "../utils";
import { getBlockBoundaryState } from "./blockBoundary";
import { getContext } from "./context";
import { AI_TOPICS_DECO_ORDER } from "./prompt";

import type {
	aiTopicsGenerateTopicsFromHeadingMutation,
	aiTopicsGenerateTopicsFromHeadingMutation$data,
} from "~/__generated__/aiTopicsGenerateTopicsFromHeadingMutation.graphql";

import styles from "./aiTopics.module.css";

export const AI_TOPICS_TEMPLATE_ID = "ai-topics-template";

export const aiProseKey = "ai-topics";
export const aiTopicsPluginKey = new PluginKey<AITopicsState>(aiProseKey);

type LoadingArea = "all" | "dropdown" | "none";

type AITopicsStateValue = {
	headerText: string;
	subheading?: string;
	generatedTopics: string[];
	// This is an annoyingly necessary flag to address the fact that we can't determine whether
	// a length of 1 generated topic is because it's the initial state,
	// or that all other topics have been inserted into the note
	additionalTopicsAreLoaded: boolean;
	loadingArea?: LoadingArea;
	didError?: boolean;
};
type AITopicsState = Map<string, AITopicsStateValue>;

const generateTopicsMutation = graphql`
	mutation aiTopicsGenerateTopicsFromHeadingMutation($heading: String!, $subheading: String!, $noteId: ID!) {
		generateTopicsFromHeading(heading: $heading, subheading: $subheading, noteId: $noteId) {
			topics
		}
	}
`;

export const getDiscussionTopicsFromHeading = async (
	heading: string,
	noteId: string,
	subheading?: string,
): Promise<string[] | Error> => {
	let result: aiTopicsGenerateTopicsFromHeadingMutation$data = { generateTopicsFromHeading: null };
	let aiTopics;
	try {
		result = await commitMutationPromise<aiTopicsGenerateTopicsFromHeadingMutation>(environment, {
			mutation: generateTopicsMutation,
			variables: {
				heading,
				subheading: subheading ?? "",
				noteId,
			},
		});
	} catch (e) {
		// TODO (ER): do we want to send the caught error to Sentry?
		return new Error("An error occured while generating topics");
	} finally {
		aiTopics = result.generateTopicsFromHeading?.topics;
	}
	return [...(aiTopics ?? [])];
};

function insertTalkingPointTr(state: EditorState, tr: Transaction, pos: number, textContent: string) {
	const talkingPointNode: ProseNode = state.schema.nodes.agenda.createChecked(
		{ blockId: v4() },
		state.schema.text(textContent),
	);
	tr.insert(pos, talkingPointNode);
	return tr;
}

const AgendaBuilderTopicKey = "AgendaBuilder__Topic";

const TopicPreview = (props: {
	position: number;
	blockId: string;
	aiTopicsStateValue: AITopicsStateValue;
	view: EditorView;
	isFirstHeader: boolean;
}) => {
	const { isMobile } = React.useContext(ViewportContext);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const toggleMenu = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		// This is a janky way to calculate the width of the menu based on the width of the parent element.
		// The parentelement.parentElement should be the TopicPreview component with the first talking point
		const parentElementWidth = e.currentTarget?.parentElement?.parentElement?.clientWidth;
		setMenuWidth(parentElementWidth ? `${parentElementWidth}px` : "50vw");
		setIsOpen(isOpen => !isOpen);
	}, []);
	const closeMenu = useCallback(() => setIsOpen(false), []);
	const [menuWidth, setMenuWidth] = useState<string>();

	const { view } = props;
	const { headerText, subheading, generatedTopics, loadingArea, didError } = props.aiTopicsStateValue;
	const firstTopic = generatedTopics[0];
	let displayedTopics = generatedTopics.slice(1);
	if (displayedTopics.length > 3) {
		displayedTopics = displayedTopics.slice(1, 4);
	}
	const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
	const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

	const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
		placement: "bottom-end",
	});

	const blurRef = useRef<HTMLDivElement | null>(null);

	const setPopperElementAndBlurRef = useCallback(
		(el: HTMLDivElement) => {
			setPopperElement(el);
			blurRef.current = el;
		},
		[setPopperElement, blurRef],
	);

	const closeMenuOnBlur = useCallback(() => {
		if (isOpen) {
			closeMenu();
		}
	}, [closeMenu, isOpen]);

	useBlur(blurRef, closeMenuOnBlur);

	const { dismiss, hasDismissed } = useContext(DismissibleElementsContext);
	const dismissTooltip = useCallback(() => {
		dismiss(AgendaBuilderTopicKey);
	}, [dismiss]);
	const showTooltip = useMemo(
		() => !hasDismissed(AgendaBuilderTopicKey) && props.isFirstHeader,
		[hasDismissed, props.isFirstHeader],
	);

	const onClickAITopic = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (e.defaultPrevented) {
				return;
			}

			const topicText = getHTMLElement(e.currentTarget, "[data-topic-text-container]").innerText;
			const pos = props.position;
			let tr = view.state.tr;

			const currentPluginState: AITopicsState = aiTopicsPluginKey.getState(view.state) ?? new Map();
			const blockState = currentPluginState?.get(props.blockId);
			if (!blockState) return;

			// Determine which topic was clicked and remove it from the generatedTopics array
			const updatedTopics = blockState.generatedTopics.filter(topic => topic !== topicText);

			const updatedTopicsState = currentPluginState.set(props.blockId, {
				...blockState,
				generatedTopics: updatedTopics,
				// If initial talking point inserted before remaining are loaded, show "all" loading area
				loadingArea:
					updatedTopics.length === 0 && !blockState.additionalTopicsAreLoaded ? "all" : blockState.loadingArea,
			});

			tr = insertTalkingPointTr(view.state, tr, pos, topicText);
			tr.setMeta("agendaBuilderInsertTopic", true);

			tr.setMeta(aiTopicsPluginKey, updatedTopicsState);
			view.dispatch(tr);

			dismissTooltip();
			closeMenu();
		},
		[closeMenu, dismissTooltip, props.blockId, props.position, view],
	);

	const onClickReloadSuggestions = React.useCallback(() => {
		const extension = view.extensions.get(aiProseKey);
		if (extension instanceof AITopicsExtension) {
			extension.reloadSuggestionsForHeading(headerText, props.blockId, subheading);
		}
		closeMenu();
	}, [closeMenu, headerText, props.blockId, subheading, view.extensions]);

	return (
		<>
			{didError ? (
				<div className={styles.loadingPreview}>
					<FontAwesomeIcon icon={faExclamationCircle} className={styles.aiTopicsIcon} />
					<div className={styles.topicText}>An error occurred while generating talking points</div>
					<Button onClick={onClickReloadSuggestions} className={styles.retryButton}>
						Retry
					</Button>
				</div>
			) : loadingArea === "all" ? (
				<div className={styles.loadingPreview}>
					<FontAwesomeIcon icon={faSpinner} className={styles.aiTopicsIcon} fixedWidth pulse />
					<div className={styles.topicText}>Generating talking points...</div>
				</div>
			) : generatedTopics.length === 0 ? (
				<div className={styles.loadingPreview} onClick={onClickReloadSuggestions}>
					<FontAwesomeIcon icon={faSync} className={styles.aiTopicsIcon} />
					<div className={styles.topicText}>Generate more talking points</div>
				</div>
			) : (
				<>
					<Tooltip
						visible={showTooltip}
						interactive
						content={
							<DismissibleTooltip
								dismiss={dismissTooltip}
								icon={faSparkles}
								text="Click to add a suggested topic for your heading to the agenda."
							/>
						}
					>
						<div
							className={styles.topicPreview}
							onClick={onClickAITopic}
							data-test-id={`${AGENDA_BUILDER_FEATURE_FLAG}-first-talking-point`}
						>
							<FontAwesomeIcon icon={faSparkles} className={cx(styles.aiTopicsIcon, styles.aiIcon)} fixedWidth />
							<FontAwesomeIcon icon={faPlus} className={cx(styles.aiTopicsIcon, styles.plusIcon)} fixedWidth />

							<div className={styles.topicText} data-topic-text-container>
								{firstTopic}
							</div>

							<div ref={setReferenceElement} className={styles.viewMoreButtonContainer}>
								<Button
									className={styles.viewMoreButton}
									onClick={toggleMenu}
									key={props.blockId}
									data-test-id={`${AGENDA_BUILDER_FEATURE_FLAG}-view-more-talking-points`}
								>
									{isMobile ? "More" : "View more"}
									<FontAwesomeIcon icon={faCaretDown} />
								</Button>
							</div>
						</div>
					</Tooltip>
					{isOpen && (
						<div
							ref={setPopperElementAndBlurRef}
							style={{ ...popperStyles.popper, width: menuWidth || "50vw" }}
							{...attributes.popper}
							className={styles.viewMoreContent}
						>
							{loadingArea === "dropdown" ? (
								<div className={styles.dropdownLoading}>
									<FontAwesomeIcon icon={faSpinner} className={styles.aiTopicsIcon} fixedWidth pulse />
									Generating additional talking points...
								</div>
							) : (
								<>
									{displayedTopics.map(topic => (
										<div
											className={styles.topicPreview}
											key={`${props.blockId}-${topic}`}
											onClick={onClickAITopic}
											data-test-id={`${AGENDA_BUILDER_FEATURE_FLAG}-dropdown-talking-point`}
										>
											<FontAwesomeIcon
												icon={faSparkles}
												className={cx(styles.aiTopicsIcon, styles.aiIcon)}
												fixedWidth
											/>
											<FontAwesomeIcon icon={faPlus} className={cx(styles.aiTopicsIcon, styles.plusIcon)} fixedWidth />
											<div className={styles.topicText} data-topic-text-container>
												{topic}
											</div>
										</div>
									))}
									<hr />
									<div
										className={cx(styles.reloadOption, styles.topicPreview)}
										onClick={onClickReloadSuggestions}
										data-test-id={`${AGENDA_BUILDER_FEATURE_FLAG}-reload-talking-points`}
									>
										<FontAwesomeIcon icon={faSync} className={cx(styles.aiTopicsIcon, styles.reloadIcon)} />
										Reload suggestions
									</div>
								</>
							)}
						</div>
					)}
				</>
			)}
		</>
	);
};

export class AITopicsExtension extends Extension {
	name = aiProseKey;
	topicPreviewDomNodes = new Map<string, HTMLElement>(); // blockId, domElement
	noteId: string;
	// This gives us more control over the state updates caused by AI Topics requests
	aiRequestsInProgress = 0;

	constructor({ noteId }: { noteId: string }) {
		super();
		this.noteId = noteId;
	}

	insertInitialTalkingPointForHeader(
		blockId: string,
		heading: string,
		talkingPoint: string,
		tr: Transaction,
		insertPos: number,
		node: ProseNode,
		subheading?: string,
	) {
		const view = this.editorView;
		invariant(view);
		const state = view.state;

		const topicsState = aiTopicsPluginKey.getState(state) ?? new Map();
		topicsState.set(blockId, {
			headerText: heading,
			subheading: subheading,
			generatedTopics: [talkingPoint],
			additionalTopicsAreLoaded: false,
			loadingArea: "dropdown",
			didError: false,
		});

		tr.setMeta(aiTopicsPluginKey, topicsState);
		tr.insert(insertPos, node);
		view.dispatch(tr);
		this.debouncedCalculateAITopicsState(false);
	}

	getHeadingAndSubheadingFromNode(node: ProseNode): { heading: string; subheading: string } {
		let hardBreakPos = -1;

		node.forEach((child, pos) => {
			if (child.type.name === "hard_break") {
				hardBreakPos = pos;
			}
		});

		let heading = "";
		let subheading = "";
		if (hardBreakPos > -1) {
			heading = node.textBetween(0, hardBreakPos);
			subheading = node.textBetween(hardBreakPos + 1, node.content.size);
		} else {
			heading = node.textContent;
		}
		return { heading: heading, subheading: subheading };
	}

	calculateAITopicsState = async (updateTopicsForSettings: boolean): Promise<AITopicsState> => {
		const view = this.editorView;
		invariant(view);
		const state = view.state;
		const { doc } = state;
		const previousTopicsPerHeading = aiTopicsPluginKey.getState(view.state) ?? new Map();
		const newAITopicsState: Promise<[blockId: string, value: AITopicsStateValue]>[] = [];

		blockDescendants(doc, node => {
			if (node.type.name === "header-one") {
				const { blockId } = node.attrs;
				const { heading: headerText, subheading: subheadingText } = this.getHeadingAndSubheadingFromNode(node);
				if (!headerText) return;
				const prevHeadingState: AITopicsStateValue | undefined = previousTopicsPerHeading.get(blockId);
				if (prevHeadingState) {
					// Determine if heading or subheading have changed
					const prevHeaderText = prevHeadingState.headerText;
					const prevSubheadingText = prevHeadingState.subheading;
					if (
						prevHeaderText === headerText &&
						prevSubheadingText === subheadingText &&
						prevHeadingState.additionalTopicsAreLoaded &&
						!updateTopicsForSettings
					) {
						// If the heading and subheading have not changed, we don't need to recompute the topics
						// however, it's possible that the state update occurred because the initial topic was inserted
						// before the remainder were loaded. So, check for additionalTopicsAreLoaded
						newAITopicsState.push(Promise.resolve([blockId, prevHeadingState]));
					} else {
						let prevTopics: string[] = [];
						prevTopics = prevHeadingState?.generatedTopics ?? [];

						if (prevHeadingState.additionalTopicsAreLoaded) {
							// Additional topics have been previously loaded, but heading or subheading have changed
							this.setLoadingStateForBlockId(blockId, previousTopicsPerHeading, "all");
						}

						const topics = getDiscussionTopicsFromHeading(headerText, this.noteId, subheadingText);

						newAITopicsState.push(
							topics.then(topics => {
								if (topics instanceof Error) {
									return [
										blockId,
										{
											headerText: headerText,
											subheading: subheadingText,
											generatedTopics: [...prevTopics],
											loadingArea: "none",
											didError: true,
											additionalTopicsAreLoaded: true,
										},
									];
								}

								return [
									blockId,
									{
										headerText: headerText,
										subheading: subheadingText,
										generatedTopics: prevHeadingState.additionalTopicsAreLoaded
											? [...topics]
											: uniq([...prevTopics, ...topics]),
										loadingArea: "none",
										didError: false,
										additionalTopicsAreLoaded: true,
									},
								];
							}),
						);
					}
				} else {
					previousTopicsPerHeading.set(blockId, {
						headerText: headerText,
						subheading: subheadingText,
						generatedTopics: [],
					});
					this.setLoadingStateForBlockId(blockId, previousTopicsPerHeading, "all");
					const topics = getDiscussionTopicsFromHeading(headerText, this.noteId, subheadingText);
					newAITopicsState.push(
						topics.then(topics => {
							if (topics instanceof Error) {
								return [
									blockId,
									{
										headerText: headerText,
										subheading: subheadingText,
										generatedTopics: [],
										loadingArea: "none",
										didError: true,
										additionalTopicsAreLoaded: true,
									},
								];
							}

							return [
								blockId,
								{
									headerText: headerText,
									subheading: subheadingText,
									generatedTopics: topics,
									loadingArea: "none",
									didError: false,
									additionalTopicsAreLoaded: true,
								},
							];
						}),
					);
				}
			}
		});

		const resolvedTopicsState = await Promise.all(newAITopicsState);
		return new Map([...resolvedTopicsState]);
	};

	debouncedCalculateAITopicsState = debounce((updateTopicsForSettings: boolean) => {
		this.aiRequestsInProgress++;
		this.calculateAITopicsState(updateTopicsForSettings)
			.finally(() => this.aiRequestsInProgress--)
			.then(newTopicsState => {
				// If there are still requests in progress, don't update the state
				if (this.aiRequestsInProgress > 0) return;
				const view = this.editorView;
				invariant(view);
				let newTr = view.state.tr;
				newTr.setMeta(aiTopicsPluginKey, newTopicsState);
				newTr.setMeta("agendaBuilderTopicsCalculated", true);
				view.dispatch(newTr);
			});
	}, 500);

	getAIHeaderDecorations(state: EditorState) {
		if (!getContext(state).inAgendaBuilderMode) return null;
		const topicsState = aiTopicsPluginKey.getState(state);
		if (!topicsState) return null;

		const blockBoundaryState = getBlockBoundaryState(state);
		const dismissibleElementsContext = getContext(state)?.dismissibleElementsContext;
		const { blockBoundaries } = blockBoundaryState || {};
		invariant(blockBoundaries);
		const decorations: Decoration[] = [];

		// TODO (ER): May want to check if there are any topicPreviewDomNodes that should be removed
		if (!getContext(state).generatedHeaders || getContext(state).generatedHeaders.length === 0) {
			return DecorationSet.empty;
		}

		const minHeaderStartPos = Math.min(
			...Object.values(blockBoundaries)
				.filter(([_s, _e, node]) => node.type.name === "header-one")
				.map(([start]) => start),
		);

		for (const [blockId, value] of topicsState) {
			const blockBoundary = blockBoundaries[blockId];
			if (
				!value.generatedTopics ||
				(value.generatedTopics.length === 0 && !value.loadingArea && !value.didError) ||
				!blockBoundary
			)
				continue;
			const [start, end] = blockBoundary;

			const decoration = Decoration.widget(
				end,
				view => {
					// Managing the dom nodes at the class level using topicPreviewDomNodes prevents complete
					// re-rendering of the topic preview component when the state changes, which would cause the open menu to
					// become untethered from its trigger
					const div = this.topicPreviewDomNodes.get(blockId) ?? document.createElement("div");
					ReactDOM.render(
						<RelayEnvironmentProvider environment={environment}>
							<DismissibleElementsContext.Provider value={dismissibleElementsContext}>
								<TopicPreview
									position={end}
									blockId={blockId}
									aiTopicsStateValue={value}
									view={view}
									isFirstHeader={start === minHeaderStartPos}
								/>
							</DismissibleElementsContext.Provider>
						</RelayEnvironmentProvider>,
						div,
					);
					this.topicPreviewDomNodes.set(blockId, div);
					return div;
				},
				{ [blockId]: value.generatedTopics[0], side: AI_TOPICS_DECO_ORDER },
			);
			decorations.push(decoration);
		}

		return DecorationSet.create(state.doc, decorations);
	}

	setLoadingStateForBlockId(blockId: string, previousAITopicsState: AITopicsState, loadingArea: LoadingArea) {
		const view = this.editorView;
		invariant(view);

		const prevBlockState = previousAITopicsState.get(blockId);
		if (!prevBlockState) return;
		const loadingStateTr = view.state.tr;
		const loadingTopicsState = previousAITopicsState.set(blockId, { ...prevBlockState, loadingArea });

		loadingStateTr.setMeta(aiTopicsPluginKey, loadingTopicsState);
		view.dispatch(loadingStateTr);
	}

	reloadSuggestionsForHeading = async (headingText: string, blockId: string, subheadingText?: string) => {
		const view = this.editorView;
		invariant(view);

		const currentTopicsState: AITopicsState | undefined = aiTopicsPluginKey.getState(view.state);

		if (currentTopicsState) {
			this.setLoadingStateForBlockId(blockId, currentTopicsState, "all");
		}

		const generatedTopicsState: AITopicsStateValue = await getDiscussionTopicsFromHeading(
			headingText,
			this.noteId,
			subheadingText ?? "",
		).then(topics => {
			if (topics instanceof Error) {
				return {
					headerText: headingText,
					subheading: subheadingText,
					generatedTopics: [],
					loadingArea: "none",
					didError: true,
					additionalTopicsAreLoaded: true,
				};
			}
			return {
				headerText: headingText,
				subheading: subheadingText,
				generatedTopics: topics,
				loadingArea: "none",
				didError: false,
				additionalTopicsAreLoaded: true,
			};
		});

		let newTr = view.state.tr;
		const newTopicsState = new Map([...(currentTopicsState ?? []), [blockId, generatedTopicsState]]);
		newTr.setMeta(aiTopicsPluginKey, newTopicsState);
		view.dispatch(newTr);
	};

	isInitialTopicForHeaderAlreadyInserted(initialTopicText: string, headerBlockId: string) {
		const view = this.editorView;
		invariant(view);
		const { state } = view;

		let initialTopicInserted = false;

		blockDescendants(state.doc, node => {
			if (node.attrs.promptResponseTo === headerBlockId && node.textContent === initialTopicText) {
				initialTopicInserted = true;
			}
		});

		return initialTopicInserted;
	}

	get plugins() {
		const extension = this;

		return [
			new Plugin({
				key: aiTopicsPluginKey,
				appendTransaction(trs, oldState, newState) {
					const { agendaBuilderTone: oldAgendaBuilderTone, agendaBuilderContext: oldAgendaBuilderContext } =
						getContext(oldState);

					const { agendaBuilderTone: newAgendaBuilderTone, agendaBuilderContext: newAgendaBuilderContext } =
						getContext(newState);

					const aiSettingsUpdated =
						oldAgendaBuilderTone !== newAgendaBuilderTone || oldAgendaBuilderContext !== newAgendaBuilderContext;
					if (
						!getContext(newState).inAgendaBuilderMode ||
						(trs.every(tr => !tr.docChanged) &&
							!trs.some(tr => tr.getMeta("agendaBuilderInsertTopic")) &&
							!trs.some(tr => tr.getMeta("agendaBuilderTopicsCalculated")) &&
							!aiSettingsUpdated)
					)
						return;

					const currentPluginTopicsState: AITopicsState = aiTopicsPluginKey.getState(newState) ?? new Map();
					const newPluginState = new Map();

					const oldPluginTopicsState: AITopicsState | undefined = aiTopicsPluginKey.getState(oldState);
					if (oldPluginTopicsState && oldPluginTopicsState.size > currentPluginTopicsState.size) {
						// Some header topic state was removed from the document in the current state.
						// This is likely due to a state mismatch from multiple calls to calculateAITopicsState
						oldPluginTopicsState.forEach((topicsState, blockId) => {
							if (!currentPluginTopicsState.has(blockId)) {
								newPluginState.set(blockId, topicsState);
							}
						});
					}

					currentPluginTopicsState?.forEach((topicsState, blockId) => {
						const initialTopicForHeader = getContext(newState).generatedHeaders?.find(
							(h: HeaderContentType) => h.id === blockId,
						)?.talkingPoint;
						if (extension.isInitialTopicForHeaderAlreadyInserted(initialTopicForHeader, blockId)) {
							const newTopics = topicsState.generatedTopics;
							const initialTopicIdx = newTopics.indexOf(initialTopicForHeader);
							if (initialTopicIdx > -1) {
								newTopics.splice(initialTopicIdx, 1);
								newPluginState.set(blockId, { ...topicsState, generatedTopics: newTopics });
							}
						}
					});

					const updatedPluginState = new Map([...currentPluginTopicsState, ...newPluginState]);

					const tr = newState.tr;
					tr.setMeta(aiTopicsPluginKey, updatedPluginState);

					if (trs.some(tr => tr.docChanged) || aiSettingsUpdated) {
						// Text has updated, check if we need to recalculate topics
						extension.debouncedCalculateAITopicsState(aiSettingsUpdated);
					}
					return tr;
				},

				state: {
					init: _config => {
						return new Map();
					},
					apply: (tr, value: AITopicsState, _oldEditorState) => {
						// Based on meta set on this transaction, update the plugin state, or return the default
						return tr.getMeta(aiTopicsPluginKey) ?? value;
					},
				},
				props: {
					decorations: state => {
						if (!getContext(state).inAgendaBuilderMode) return;
						const topicState = aiTopicsPluginKey.getState(state);
						if (topicState) {
							return this.getAIHeaderDecorations(state);
						}
						return;
					},
				},
			}),
		];
	}

	destroy(): void {
		this.topicPreviewDomNodes.forEach(dom => {
			ReactDOM.unmountComponentAtNode(dom);
			dom.remove();
		});
		this.topicPreviewDomNodes.clear();
	}
}
